/**
 * Age-Based Profiles (§22.19).
 *
 * For a child whose birth year places them in one of the four age bands,
 * show the recommended defaults (screen time, filter level, tone, UI) and
 * let the parent apply the recommended content filters in one tap.
 *
 * The button shows an "Applied ✓" state when the child's current category
 * rules already include every recommended block — so the parent gets a
 * visible confirmation instead of a toast that disappears in 3 seconds.
 */
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Sparkles, ShieldCheck, Check } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { usersApi, type Child, type AgeProfile } from '@/api/users';
import { safetyApi } from '@/api/safety';

const FILTER_LABEL: Record<AgeProfile['web_filter_level'], string> = {
  strict: 'Strict web filter',
  moderate: 'Moderate web filter',
  light: 'Light web filter',
};

export function AgeProfilePanel({ child }: { child: Child }) {
  const queryClient = useQueryClient();
  const profiles = useQuery({
    queryKey: ['age-profiles'],
    queryFn: () => usersApi.ageProfiles(),
    staleTime: 60 * 60_000,
    retry: false,
  });
  // Current rules so we can show "Applied ✓" when the recommended set is
  // already in place. Refetches whenever the mutation invalidates it.
  const rules = useQuery({
    queryKey: ['safety-rules', child.id],
    queryFn: () => safetyApi.getRules(child.id),
    retry: false,
  });
  const [applying, setApplying] = useState(false);

  const profile = useMemo(
    () => (profiles.data ?? []).find((p) => p.group === child.age_group) ?? null,
    [profiles.data, child.age_group],
  );

  const blockedNow = useMemo(() => {
    const set = new Set<string>();
    for (const r of rules.data?.categories ?? []) {
      if (r.blocked) set.add(r.slug);
    }
    return set;
  }, [rules.data]);

  const isApplied = profile
    ? profile.blocked_categories.every((slug) => blockedNow.has(slug))
    : false;

  if (!child.age_group) {
    return (
      <p className="rounded-md border border-dashed border-[color:var(--kz-color-border-subtle)] p-3 text-xs text-[color:var(--kz-color-ink-muted)]">
        Set a birth year to get age-appropriate recommended settings.
      </p>
    );
  }
  if (!profile) return null;

  const applyFilters = async () => {
    setApplying(true);
    try {
      await safetyApi.updateCategories(
        child.id,
        profile.blocked_categories.map((slug) => ({ slug, blocked: true })),
      );
      toast.success(`Recommended ${profile.label} filters applied to ${child.name}.`);
      // Refetch rules so the button flips to the "Applied ✓" state, and
      // refresh any other safety surface (Safety page, etc.) that's open.
      await queryClient.invalidateQueries({ queryKey: ['safety-rules', child.id] });
      await queryClient.invalidateQueries({ queryKey: ['safety-rules'] });
    } catch {
      toast.error('Could not apply the recommended filters.');
    } finally {
      setApplying(false);
    }
  };

  const chips = [
    `${profile.screen_time_minutes} min/day`,
    FILTER_LABEL[profile.web_filter_level],
    `${profile.tone} tone`,
    ...(profile.ui.teen_mode ? ['Teen mode'] : []),
    ...(profile.ui.narrated_onboarding ? ['Narrated onboarding'] : []),
    ...(profile.rewards_focus ? ['Rewards focus'] : []),
    ...(profile.weekly_reports ? ['Weekly reports'] : []),
  ];

  return (
    <div className="rounded-md border border-[color:var(--kz-color-border-subtle)] bg-[color:var(--kz-color-surface-sunken)] p-3">
      <div className="flex items-center gap-2">
        <Sparkles className="size-4 text-[color:var(--kz-color-brand-primary)]" />
        <p className="flex-1 text-sm font-medium">
          {profile.label}{' '}
          <span className="text-xs font-normal text-[color:var(--kz-color-ink-muted)]">
            (ages {profile.age_min}–{profile.age_max})
          </span>
        </p>
        {isApplied && !rules.isPending && (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
            <Check className="size-3" /> Applied
          </span>
        )}
      </div>
      <p className="mt-1 text-xs text-[color:var(--kz-color-ink-secondary)]">{profile.summary}</p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {chips.map((c) => (
          <span
            key={c}
            className="rounded-full bg-[color:var(--kz-color-surface-card)] px-2 py-0.5 text-[11px] text-[color:var(--kz-color-ink-secondary)]"
          >
            {c}
          </span>
        ))}
      </div>
      <Button
        size="sm"
        variant="outline"
        className="mt-3"
        onClick={applyFilters}
        disabled={applying || isApplied}
      >
        {isApplied ? (
          <>
            <Check className="mr-1 size-3.5 text-emerald-600" />
            Filters applied
          </>
        ) : (
          <>
            <ShieldCheck className="mr-1 size-3.5" />
            {applying ? 'Applying…' : 'Apply recommended filters'}
          </>
        )}
      </Button>
    </div>
  );
}
